import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { graphql } from '../gql';
import { GetQuoteEditionRelationshipsQuery } from '../gql/graphql';
import { ArrayElement } from '@calefy-inc/utilityTypes';

export const GET_QUOTE_EDITION_RELATIONSHIPS = graphql(`
  query getQuoteEditionRelationships($quoteUuid: String!) {
    getQuoteEditionRelationships(quoteUuid: $quoteUuid) {
      previousEdition {
        uniqueID
      }
      nextEdition {
        uniqueID
      }
      reason
      description
    }
  }
`);

type RelationshipItem = ArrayElement<
  NonNullable<GetQuoteEditionRelationshipsQuery['getQuoteEditionRelationships']>
>;

export const useQuoteEditions = (quoteUuid: string | null | undefined) => {
  const [getQuoteEditionRelationships, { data, loading, error }] = useLazyQuery(
    GET_QUOTE_EDITION_RELATIONSHIPS,
  );

  useEffect(() => {
    if (quoteUuid) {
      getQuoteEditionRelationships({
        variables: {
          quoteUuid,
        },
      });
    }
  }, [quoteUuid, getQuoteEditionRelationships]);

  return { data, loading, error };
};

export const useNextQuoteEdition = (quoteUuid: string | null | undefined) => {
  const { data } = useQuoteEditions(quoteUuid);
  const [relationship, setRelationship] = useState<RelationshipItem | null>(
    null,
  );

  useEffect(() => {
    if (!data?.getQuoteEditionRelationships || !quoteUuid) {
      setRelationship(null);
      return;
    }

    // Find the relationship where the current quote is the previous edition
    // and return the whole relationship
    const foundRelationship = data.getQuoteEditionRelationships.find(
      (rel: RelationshipItem | null) =>
        rel?.previousEdition?.uniqueID === quoteUuid,
    );

    setRelationship(foundRelationship || null);
  }, [data, quoteUuid]);

  return relationship;
};
